<template src='./project-documents.html'></template>

<script>
import Vue from 'vue'
import ContextPage from '@/components/misc/context-page/ContextPage.vue'
import { getAccessToken } from '@/utils/cookie'
import tabulatorMixin from '@/mixins/tabulator'
import Tabulator from '@/components/tabulator/Tabulator'
import FileSaver from 'file-saver'
import { authorizedOrganizationId, hasAdminClaim, hasDMSManageClaim } from '@/router/auth'
import { EventBus } from '@/router/bus'
import ProjectDocumentUpload from './file-upload/ProjectDocumentUpload'

export default {
  components: {
    ContextPage,
    Tabulator,
    'project-document-upload': ProjectDocumentUpload
  },
  mixins: [tabulatorMixin],
  data () {
    return {
      data: [],
      projectCode: this.$route.params.projectCode,
      projectDocumentApi: Vue.$config.api_root + '/' + Vue.$apiConfig.projectDocumentPost.replace('{projectCode}', this.$route.params.projectCode),
      projectDocumentHeaders: {
        Authorization: 'Bearer ' + getAccessToken()
      },
      sizeLimit: 1024 * 1024 * 10,
      filesLimit: 5,
      ProjectStatus: {
        PENDING: 0,
        FINISHED: 1
      },
      project: {},
      phaseStatus: 0,
      files: [],
      description: '',
      comment: '',
      documentsCount: 0,
      revisions: [],
      revisionId: 0,
      deleteDialog: false,
      editDialog: false,
      editDialogAttributes: {
        documentId: 0,
        title: '',
        description: ''
      },
      documentId: 0,
      disableButton: false,
      selectedDocument: '',
      documentsKey: 0,
      addDocumentLabel: this.$t('projects.document.add'),
      tabulatorOptions: {
        ajaxURL: Vue.$config.api_root + '/' + Vue.$apiConfig.pagedDocumentsGet.replace('{projectCode}', this.$route.params.projectCode),
        ajaxResponse: (url, params, response) => {
          this.project = response.additionalInformation.project
          this.phaseStatus = response.additionalInformation.project.phaseStatus
          this.documentsCount = response.payload.length
          return this.ajaxResponse(url, params, response)
        },
        columns: [
          { title: '', field: 'id', visible: false, headerSort: false },
          { title: '', field: 'documentId', visible: false, headerSort: false },
          {
            title: this.$t('projects.document.name'),
            field: 'title',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            cellClick: (e, cell) => {
              this.download(cell.getRow().getData().id, cell.getRow().getData().title)
            }
          },
          {
            title: this.$t('projects.document.documentType'),
            field: 'documentType',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            headerFilter: this.vTextSelect,
            headerFilterParams: {
              values: [
                { text: this.$t('projects.document.types.0'), value: 0 },
                { text: this.$t('projects.document.types.1'), value: 1 },
                { text: this.$t('projects.document.types.2'), value: 2 },
                { text: this.$t('projects.document.types.3'), value: 3 },
                { text: this.$t('projects.document.types.4'), value: 4 },
                { text: this.$t('projects.document.types.5'), value: 5 },
                { text: this.$t('projects.document.types.6'), value: 6 },
                { text: this.$t('projects.document.types.7'), value: 7 },
                { text: this.$t('projects.document.types.8'), value: 8 },
                { text: this.$t('projects.document.types.9'), value: 9 },
                { text: this.$t('projects.document.types.10'), value: 10 },
                { text: this.$t('projects.document.types.11'), value: 11 },
                { text: this.$t('projects.document.types.12'), value: 12 },
                { text: this.$t('projects.document.types.13'), value: 13 },
                { text: this.$t('projects.document.types.14'), value: 14 },
                { text: this.$t('projects.document.types.15'), value: 15 }
              ]
            },
            cellClick: (e, cell) => {
              this.download(cell.getRow().getData().id, cell.getRow().getData().documentType)
            },
            formatter: (cell) => {
              const type = cell.getValue()
              return this.$t('projects.document.types.' + type)
            },
            tooltip: (cell) => {
              const type = cell.getValue()
              return this.$t('projects.document.types.' + type)
            }
          },
          {
            title: this.$t('projects.document.description'),
            field: 'description',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            cellClick: (e, cell) => {
              this.download(cell.getRow().getData().id, cell.getRow().getData().title)
            }
          },
          {
            title: this.$t('projects.document.revision'),
            field: 'revision',
            headerSort: true,
            headerFilterPlaceholder: this.$t('tabulator.headerFilters.default'),
            headerFilter: this.vTextFieldFilter,
            headerFilterLiveFilter: false,
            hozAlign: 'center',
            width: 100,
            cellClick: (e, cell) => {
              this.download(cell.getRow().getData().id, cell.getRow().getData().title)
            }
          },
          {
            title: this.$t('projects.document.createdDate'),
            hozAlign: 'center',
            field: 'createdDate',
            sorter: 'date',
            formatter: (cell, formatterParams, onRendered) => {
              return cell.getValue() ? this.formatDate(cell.getValue()) : this.$t('general.undefined')
            },
            headerFilter: this.vDateRange,
            headerFilterParams: { isRange: true },
            headerFilterLiveFilter: false,
            cellClick: (e, cell) => {
              this.download(cell.getRow().getData().id, cell.getRow().getData().title)
            }
          },
          {
            title: this.$t('projects.document.phase'),
            hozAlign: 'center',
            field: 'phase',
            sorter: 'date',
            headerFilter: this.vTextSelect,
            headerFilterParams: {
              values: [
                {
                  text: this.$t('projects.stages.0'),
                  value: 0
                },
                {
                  text: this.$t('projects.stages.1'),
                  value: 1
                },
                {
                  text: this.$t('projects.stages.2'),
                  value: 2
                },
                {
                  text: this.$t('projects.stages.3'),
                  value: 3
                },
                {
                  text: this.$t('projects.stages.4'),
                  value: 4
                },
                {
                  text: this.$t('projects.stages.5'),
                  value: 5
                },
                {
                  text: this.$t('projects.stages.7'),
                  value: 7
                },
                {
                  text: this.$t('projects.stages.8'),
                  value: 8
                },
                {
                  text: this.$t('projects.stages.11'),
                  value: 11
                },
                {
                  text: this.$t('projects.stages.12'),
                  value: 12
                }
              ]
            },
            formatter: (cell) => {
              const phase = cell.getValue()
              return this.$t('projects.stages.' + phase)
            },
            headerFilterLiveFilter: false,
            cellClick: (e, cell) => {
              this.download(cell.getRow().getData().id, cell.getRow().getData().title)
            }
          },
          {
            title: '',
            formatter: (cell, formatterParams, onRendered) => {
              if (cell.getRow().getData().canBeDeleted) {
                return '<button class="v-icon notranslate v-icon--link mdi mdi-delete theme--dark primary--text"></button>'
              }
            },
            headerSort: false,
            width: 20,
            align: 'center',
            cellClick: (e, cell) => {
              if (cell.getRow().getData().canBeDeleted) this.showDeletePromptDialog(cell.getRow().getData().documentId)
            }
          },
          {
            title: '',
            formatter: (cell, formatterParams, onRendered) => {
              if (cell.getRow().getData().canBeDeleted) {
                return '<button class="v-icon notranslate v-icon--link mdi mdi-file-edit theme--dark primary--text"></button>'
              }
            },
            headerSort: false,
            width: 20,
            align: 'center',
            cellClick: (e, cell) => {
              if (cell.getRow().getData().canBeDeleted) this.showEditDialog(cell.getRow().getData().documentId, cell.getRow().getData().title, cell.getRow().getData().description)
            }
          },
          {
            title: '',
            formatter: (cell, formatterParams, onRendered) => {
              return '<a href="javascript:void(0)">' + this.$t('general.details') + '</a>'
            },
            headerSort: false,
            width: 80,
            align: 'left',
            cellClick: (e, cell) => {
              this.showRevisions(cell.getRow().getData().documentId)
            }
          }
        ],
        rowContext: (e, row) => {
          e.preventDefault()
          this.copyLink(row.getData())
        }
      }
    }
  },
  computed: {
    canManageDocuments () {
      return (hasDMSManageClaim() && this.project.organizationId === authorizedOrganizationId() && this.project.status === this.ProjectStatus.PENDING) || hasAdminClaim()
    }
  },
  created () {
    EventBus.$on('uploadedDocument', (jsonScheme) => {
      if (this.$route.params.documentId) return
      this.handleUploaded()
    })
  },
  mounted () {
    EventBus.$emit('reload-crumbs')
  },
  methods: {
    download (id, title, reload) {
      this.showLoader(true)
      this.disableButton = true
      this.$http.get(Vue.$apiConfig.projectDocumentGet.replace('{projectCode}', this.$route.params.projectCode) + '/' + id, { responseType: 'arraybuffer' })
        .then(
          response => {
            var headers = response.headers
            var blob = new Blob([response.data], {
              type: headers.get('content-type')
            })
            FileSaver.saveAs(blob, title)
            this.showLoader(false)
            this.disableButton = false
          },
          error => {
            this.formatErrorResponse(error)
            this.showLoader(false)
            this.disableButton = false
          }
        )
    },
    showRevisions (documentId) {
      this.$router.push({ name: 'singleProjectdocumentsrevisions', params: { documentId: documentId } })
    },
    handleErrors (newFile) {
      if (newFile.xhr) {
        console.log(newFile)
        this.formatDocumentErrorResponse(newFile.xhr)
      }
      this.showLoader(false)
    },
    handleUploaded () {
      this.tabulatorInstance.setData()
      this.showLoader(false)
    },
    exportAll () {
      this.showLoader(true)
      this.disableButton = true
      this.$http.get(Vue.$apiConfig.generateDocumentsGet.replace('{projectCode}', this.$route.params.projectCode), { responseType: 'blob' })
        .then(
          response => {
            const blob = new Blob([response.data], { type: 'application/zip' })
            FileSaver.saveAs(blob, this.projectCode + '-dokumenta.zip')
            this.showLoader(false)
            this.disableButton = false
          },
          error => {
            this.formatErrorResponse(error)
            this.showLoader(false)
            this.disableButton = false
          }
        )
    },
    showDeletePromptDialog (documentId) {
      this.deleteDialog = true
      this.documentId = documentId
    },

    showEditDialog (documentId, title, description) {
      this.editDialog = true
      this.editDialogAttributes.documentId = documentId
      this.editDialogAttributes.title = title
      this.editDialogAttributes.description = description
    },
    deleteDocument () {
      this.showLoader(true)
      this.disableButton = true
      this.$http.delete(Vue.$apiConfig.projectDocumentRevisionsDelete.replace('{projectCode}', this.$route.params.projectCode).replace('{documentId}', this.documentId))
        .then(
          response => {
            this.showLoader(false)
            this.tabulatorInstance.setData()
            this.disableButton = false
            this.documentId = 0
            this.deleteDialog = false
          },
          error => {
            this.formatErrorResponse(error)
            this.showLoader(false)
            this.disableButton = false
          }
        )
    },
    editDocument () {
      this.showLoader(true)
      this.disableButton = true
      this.$http.post(Vue.$apiConfig.projectDocumentEdit.replace('{projectCode}', this.$route.params.projectCode).replace('{documentId}', this.editDialogAttributes.documentId), this.editDialogAttributes)
        .then(
          response => {
            this.showLoader(false)
            this.tabulatorInstance.setData()
            this.disableButton = false
            this.documentId = 0
            this.editDialog = false
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('contextPath.editSuccess')
            })
          },
          error => {
            this.formatErrorResponse(error)
            this.showLoader(false)
            this.disableButton = false
            this.editDialog = false
          }
        )
    },
    showLoader  (loader) {
      EventBus.$emit('projectLoader', loader)
    }
  }
}
</script>

<style>
.v-chip {
  font-weight: 600 !important;
  /* font-size: 20px !important */
}
.item-container {
  font-weight: 600 !important;
  font-size: 15px !important;
}

.drop-menu-item {
  margin: 0 0 0 50px;
}
.claim-item {
  font-size: 15px;
  font-weight: 300;
  line-height: 1;
  letter-spacing: 0.02em;
  font-family: 'Source Sans Pro', sans-serif;
  margin-left: 30px;
}

.centered-label {
  display: flex;
  justify-content: center;
  flex-direction: column;
}
.list-container {
  height: 264px;
  overflow-y: auto;
  font-size: 11pt;
  border: 1px solid rgba(0, 0, 0, .12);
}
.clickable-list {
  cursor: pointer;
}
.dense-list {
  min-height: 22px !important;
}
</style>
